import { type Scene, type AnimationClip } from 'three'
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js'
import { AnimationRetargetService } from '../AnimationRetargetService'
import { ExportValidation } from '../../lib/processes/export-to-file/ExportValidation'
import { ModalDialog } from '../../lib/ModalDialog'
import { format_export_error } from '../../lib/processes/export-to-file/StepExportToFile'

export class StepExportRetargetedAnimations extends EventTarget {
  public animation_clips_to_export: AnimationClip[] = []

  public set_animation_clips_to_export (all_animations_clips: AnimationClip[], animation_checkboxes: number[]): void {
    this.animation_clips_to_export = []
    animation_checkboxes.forEach((indx) => {
      const original_clip: AnimationClip = all_animations_clips[indx]
      const cloned_clip: AnimationClip = original_clip.clone()
      this.animation_clips_to_export.push(cloned_clip)
    })
  }

  public export (filename = 'exported_model'): void {
    const selected_clip_validation = ExportValidation.validate_scene_export(
      AnimationRetargetService.getInstance().get_target_armature(),
      this.animation_clips_to_export
    )
    if (!selected_clip_validation.valid) {
      new ModalDialog('Export Validation', ExportValidation.to_html(selected_clip_validation)).show()
      return
    }

    // Retarget all animation clips before export
    let retargeted_clips: AnimationClip[] = []
    retargeted_clips = this.animation_clips_to_export.map((clip) =>
      AnimationRetargetService.getInstance().retarget_animation_clip(clip)
    )
    const target_rig_scene: Scene = AnimationRetargetService.getInstance().get_target_armature()
    const retargeted_clip_validation = ExportValidation.validate_scene_export(target_rig_scene, retargeted_clips)
    if (!retargeted_clip_validation.valid) {
      new ModalDialog('Export Validation', ExportValidation.to_html(retargeted_clip_validation)).show()
      return
    }

    this.export_glb(target_rig_scene, retargeted_clips, filename)
      .catch((error) => {
        new ModalDialog('Export failed', format_export_error(error)).show()
      })
  }

  public async export_glb (exported_scene: Scene, animations_to_export: AnimationClip[], file_name: string): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      const gltf_exporter = new GLTFExporter()

      const export_options = {
        binary: true,
        onlyVisible: false,
        embedImages: true,
        animations: animations_to_export
      }

      gltf_exporter.parse(
        exported_scene,
        (result: ArrayBuffer | { [key: string]: unknown }) => {
          // Handle the result of the export
          if (result instanceof ArrayBuffer) {
            this.save_array_buffer(result, `${file_name}.glb`)
            resolve() // Resolve the promise when the export is complete
          } else {
            reject(new Error('Export result is not an ArrayBuffer'))
          }
        },
        (error: any) => {
          reject(error) // Reject the promise if an error occurs
        },
        export_options
      )
    })
  }

  private save_file (blob: Blob, filename: string): void {
    const export_button_hidden_link: HTMLAnchorElement | null = document.querySelector('#download-hidden-link')
    if (export_button_hidden_link != null) {
      export_button_hidden_link.href = URL.createObjectURL(blob)
      export_button_hidden_link.download = filename
      export_button_hidden_link.click()
    } else {
      throw new Error('Export download link is not available.')
    }
  }

  // used for GLB to turn content into a byte array for saving
  private save_array_buffer (buffer: ArrayBuffer, filename: string): void {
    this.save_file(new Blob([buffer], { type: 'application/octet-stream' }), filename)
  }
}
